<template>
  <el-dialog
    title="派发训练"
    :visible.sync="innerVisible"
    width="720px"
    @open="loadOptions"
    @closed="resetForm"
  >
    <el-form :model="form" label-width="110px" size="small" v-loading="loading">
      <el-form-item label="任务名" required>
        <el-input v-model="form.run_name" placeholder="如 seg-0920-a" clearable />
        <div class="form-hint">
          只能用字母数字与 _ . -（以字母数字开头，不超过 64 位）；
          不能重名
        </div>
      </el-form-item>

      <el-form-item label="数据集" required>
        <el-select v-model="form.out_name" placeholder="请选择数据集版本" style="width:100%">
          <el-option
            v-for="item in datasets"
            :key="item.ref"
            :label="datasetLabel(item)"
            :value="item.ref"
          >
            <span class="option-row">
              <span class="option-main">{{ datasetLabel(item) }}</span>
              <el-button
                v-if="item.cache_state === 'missing'"
                type="text"
                size="mini"
                :disabled="pullingRefs.includes(item.ref)"
                @click.stop="handleMaterialize(item)"
              >下载到本地</el-button>
              <span v-else-if="item.cache_state === 'pulling'" class="option-side">
                {{ pullPercent(item) }}
              </span>
            </span>
          </el-option>
        </el-select>
      </el-form-item>

      <el-form-item label="基础权重" required>
        <el-select v-model="form.model_id" placeholder="请选择基础权重" style="width:100%">
          <el-option
            v-for="item in models"
            :key="item.model_id"
            :label="item.name"
            :value="item.model_id"
          />
        </el-select>
        <div v-if="!loading && !models.length" class="form-hint warn">
          还没有基础权重，去「模型实例」页上传一个 .pt
        </div>
      </el-form-item>

      <el-form-item label="使用显卡" required>
        <el-select
          v-model="form.gpu_ids"
          multiple
          placeholder="请选择显卡"
          style="width:100%"
        >
          <el-option
            v-for="card in gpus"
            :key="card.index"
            :label="gpuLabel(card)"
            :value="card.index"
            :disabled="Boolean(card.occupied_by)"
          />
        </el-select>
      </el-form-item>

      <el-form-item label="训练轮数">
        <el-input-number v-model="form.epochs" :min="1" :max="1000" controls-position="right" />
      </el-form-item>
      <el-form-item label="batch">
        <el-input-number v-model="form.batch" :min="1" :max="256" controls-position="right" />
      </el-form-item>
      <el-form-item label="imgsz">
        <el-input-number v-model="form.imgsz" :min="1" :max="4096" :step="32" controls-position="right" />
      </el-form-item>
      <el-form-item label="workers">
        <el-input-number v-model="form.workers" :min="1" :max="64" controls-position="right" />
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <div v-if="notReadyHint" class="footer-warn">{{ notReadyHint }}</div>
      <el-button size="small" @click="innerVisible = false">取消</el-button>
      <el-button
        type="primary"
        size="small"
        :loading="dispatching"
        :disabled="!canDispatch"
        @click="handleDispatch"
      >派发</el-button>
    </div>
  </el-dialog>
</template>

<script>
import {
  fetchTrainDatasets,
  fetchTrainGpus,
  fetchTrainModels,
  dispatchRun,
  materializeDataset,
} from '../api/blissrose/training';

const DEFAULT_FORM = {
  run_name: '',
  out_name: '',
  model_id: '',
  gpu_ids: [],
  epochs: 300,
  batch: 64,
  imgsz: 640,
  workers: 8,
};

// 下载进度轮询间隔；一次只问一版清单
const PULL_POLL_MS = 2000;
// 连续问不通几次就停，免得提示刷满页面
const PULL_POLL_MAX_FAILURES = 3;

/** 派发训练对话框：数据集/显卡/基础权重三个下拉；未下载的版本仍可选，能不能训由训练机判 */
export default {
  name: 'TrainingDispatchDialog',

  props: {
    visible: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      dispatching: false,
      datasets: [],
      models: [],
      gpus: [],
      // 正在等落地的那几版
      pullingRefs: [],
      pollTimer: null,
      pollFailures: 0,
      form: { ...DEFAULT_FORM },
    };
  },

  computed: {
    innerVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        this.$emit('update:visible', val);
      },
    },

    canDispatch() {
      const { run_name: runName, out_name: outName, model_id: modelId, gpu_ids: gpuIds } = this.form;
      return Boolean(runName && outName && modelId && gpuIds.length);
    },

    selectedDataset() {
      return this.datasets.find(item => item.ref === this.form.out_name) || null;
    },

    /** 选中版本未就绪时的提醒，贴在派发按钮旁；不禁用按钮 */
    notReadyHint() {
      const item = this.selectedDataset;
      if (!item || item.cache_state === 'ready') return '';
      if (item.cache_state === 'pulling') {
        const progress = this.pullPercent(item);
        return `这一版还在下载到训练机的路上${progress ? `（${progress}）` : ''}，下载完才能派发`;
      }
      if (item.cache_state === 'missing') {
        const base = '这一版还没下载到本地，先点它那行的「下载到本地」';
        return item.cache_error ? `${base}（上次下载失败：${item.cache_error}）` : base;
      }
      return '本地状态读不出来，稍后重试或先下载一次';
    },
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    /** 一次性拉三个下拉的数据源；某个拉不到时只让那一个下拉为空，不挡其余 */
    async loadOptions() {
      this.loading = true;
      await Promise.all([this.loadDatasets(), this.loadModels(), this.loadGpus()]);
      this.loading = false;
      this.form.run_name = this.suggestRunName();
      // 打开时已在下载的版本接着往下看
      this.pullingRefs = this.datasets
        .filter(item => item.cache_state === 'pulling')
        .map(item => item.ref);
      if (this.pullingRefs.length) this.startPolling();
    },

    async loadDatasets() {
      try {
        const { items } = (await fetchTrainDatasets()).data;
        this.datasets = items || [];
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      }
    },

    async loadModels() {
      try {
        const { items } = (await fetchTrainModels()).data;
        this.models = items || [];
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      }
    },

    async loadGpus() {
      try {
        const { items } = (await fetchTrainGpus()).data;
        this.gpus = items || [];
      } catch (err) {
        // 错误提示由响应拦截器统一弹出
      }
    },

    resetForm() {
      this.stopPolling();
      this.pullingRefs = [];
      this.form = { ...DEFAULT_FORM };
      this.datasets = [];
      this.models = [];
      this.gpus = [];
    },

    /** 任务名种子：日期＋时分 */
    suggestRunName() {
      const now = new Date();
      const pad = n => String(n).padStart(2, '0');
      return `seg-${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    },

    async handleDispatch() {
      this.dispatching = true;
      try {
        const { warnings } = (await dispatchRun({ ...this.form })).data;
        this.$message.success('训练已派发');
        this.innerVisible = false;
        this.$emit('dispatched');
        // 警告不阻止派发，只随提示给出
        if (warnings && warnings.length) {
          this.$notify({
            title: '派发已提交，但有几点需要注意',
            message: warnings.join('；'),
            type: 'warning',
            duration: 0,
          });
        }
      } catch (err) {
        // 拒绝原因由响应拦截器弹出
      } finally {
        this.dispatching = false;
      }
    },

    // ====== 下载到本地 ======

    /** 下载该版本到训练机本地并跟踪落地状态；平台立刻返回，此处只记入轮询名单 */
    async handleMaterialize(item) {
      this.startPolling();
      if (!this.pullingRefs.includes(item.ref)) this.pullingRefs.push(item.ref);
      try {
        const { state } = (await materializeDataset(item.ref)).data;
        if (state === 'ready') {
          this.$message.success(`${item.ref} 训练机上已经有了，不必再下载`);
          this.dropPulled(item.ref);
          await this.loadDatasets();
          return;
        }
        this.$message.success(`已开始下载 ${item.ref}，进度见列表（完成前该行标「下载中」）`);
      } catch (err) {
        // 拒绝原因由拦截器弹出；该版本移出轮询名单
        this.pullingRefs = this.pullingRefs.filter(ref => ref !== item.ref);
        if (!this.pullingRefs.length) this.stopPolling();
      }
    },

    startPolling() {
      if (this.pollTimer) return;
      this.pollFailures = 0;
      this.pollTimer = setInterval(this.pollOnce, PULL_POLL_MS);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.pollFailures = 0;
    },

    /** 一轮轮询：刷新清单，把已落地或已失败的版本摘出名单；直接发请求，这里要能数得出问不通 */
    async pollOnce() {
      if (!this.pullingRefs.length) {
        this.stopPolling();
        return;
      }
      let items;
      try {
        items = (await fetchTrainDatasets()).data.items || [];
        this.pollFailures = 0;
      } catch (err) {
        this.pollFailures += 1;
        if (this.pollFailures >= PULL_POLL_MAX_FAILURES) this.stopPolling();
        return;
      }
      this.datasets = items;
      const byRef = new Map(items.map(item => [item.ref, item]));
      const pending = [];
      for (const ref of this.pullingRefs) {
        const item = byRef.get(ref);
        if (!item) continue; // 清单里已无该版本
        if (item.cache_state === 'ready') {
          this.$message.success(`${ref} 已下载到训练机本地`);
          continue;
        }
        if (item.cache_state === 'pulling') {
          pending.push(ref);
          continue;
        }
        // 既没就绪又没在下载：这一趟没成
        this.$notify({
          title: '下载没成',
          message: `${ref}：${item.cache_error || '平台没记下原因'}`,
          type: 'error',
          duration: 0,
        });
      }
      this.pullingRefs = pending;
      if (!pending.length) this.stopPolling();
    },

    /** 把该版本移出轮询名单：平台响应已当场确认，无须再由轮询复核 */
    dropPulled(ref) {
      this.pullingRefs = this.pullingRefs.filter(item => item !== ref);
      if (!this.pullingRefs.length) this.stopPolling();
    },

    // ====== 展示辅助 ======

    /** 下拉一行：`名@版本`＋帧数类别数＋未就绪标识；一律说「帧」不说「张图」 */
    datasetLabel(item) {
      const parts = [];
      if (item.frame_count) parts.push(`${item.frame_count} 帧`);
      if (item.class_count) parts.push(`${item.class_count} 类`);
      // 就绪不挂字，未就绪才说话（见 notReadyHint）
      const marks = { ready: '', missing: ' · 未下载到本地', pulling: ' · 下载中' };
      const mark = item.cache_state in marks ? marks[item.cache_state] : ' · 状态未知';
      const info = parts.length ? `（${parts.join('，')}）` : '';
      return `${item.ref}${info}${mark}`;
    },

    /** 已下载多少；总数没报出前不显示百分比 */
    pullPercent(item) {
      const total = item.cache_total || 0;
      if (!total) return '';
      return `${Math.floor(((item.cache_done || 0) / total) * 100)}%`;
    },

    /** 显存与占用任务一并显示 */
    gpuLabel(card) {
      const parts = [`卡 ${card.index}`];
      if (card.name) parts.push(card.name);
      if (card.memory_used !== null && card.memory_used !== undefined && card.memory_total) {
        parts.push(`${card.memory_used}/${card.memory_total} MiB`);
      }
      if (card.occupied_by) parts.push(`被 ${card.occupied_by} 占用`);
      return parts.join(' · ');
    },
  },
};
</script>

<style scoped>
.form-hint {
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 4px;
}

.form-hint.warn {
  color: #e6a23c;
}

.dialog-footer {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dialog-footer .footer-warn {
  flex: 1;
  text-align: left;
  color: #e6a23c;
  font-size: 12px;
  line-height: 1.5;
}

.option-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.option-row .option-main {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.option-row .option-side {
  color: #909399;
  font-size: 12px;
}
</style>
